import { sortBy, upperFirst } from 'lodash';
import i18next from 'i18next';

import utilsXLS from '@commons/utils/makeXLS';

import { TRANSFER_ACTIONS_RESOURCE } from '@selectors/actions/transferActions';

const RIGHT_ROW_INDENT = '  ├ ';
const ROW_TYPE = {
  PAGE: 'page',
  RIGHT: 'right',
};

const parseFeatureProps = (props) => {
  if (typeof props !== 'string') {
    return props ?? {};
  }

  try {
    return JSON.parse(props);
  } catch {
    return {};
  }
};

const isRightHiddenByFeatureProps = (right, clientFeatures) => {
  if (right.resource !== TRANSFER_ACTIONS_RESOURCE.UPDATE_DELIVERY_TIME) {
    return false;
  }

  const matchingFeature = clientFeatures.find(({ id }) => id === right.featureId);

  const featureProps = parseFeatureProps(matchingFeature?.props);

  return !featureProps.hasTransferDeliveryTime;
};

const isViewRight = (action) => action.resource?.endsWith(':view');

const getActionLabel = (action) =>
  action.translationKey ? i18next.t(action.translationKey) : action.name;

const buildAllFeatureActionsFromRoles = (roles) => {
  const actionsById = {};

  roles.forEach(({ associatedFeatures }) => {
    associatedFeatures.forEach(({ actions }) => {
      (actions ?? []).forEach((action) => {
        actionsById[action.id] = action;
      });
    });
  });

  return Object.values(actionsById);
};

const buildFeatureActionsByFeatureId = (roles, clientFeatures) => {
  const allFeatureActions = buildAllFeatureActionsFromRoles(roles);
  const featureActionsByFeatureId = {};

  allFeatureActions.forEach((action) => {
    if (isRightHiddenByFeatureProps(action, clientFeatures)) {
      return;
    }

    if (!featureActionsByFeatureId[action.featureId]) {
      featureActionsByFeatureId[action.featureId] = [];
    }

    featureActionsByFeatureId[action.featureId].push(action);
  });

  return featureActionsByFeatureId;
};

const buildAssignedActionIdsByRoleId = (roles) => {
  const assignedActionIdsByRoleId = {};

  roles.forEach((role) => {
    const assignedActionIds = new Set();

    role.associatedFeatures.forEach((feature) => {
      (feature.actions ?? []).forEach((action) => {
        assignedActionIds.add(action.id);
      });
    });

    assignedActionIdsByRoleId[role.id] = assignedActionIds;
  });

  return assignedActionIdsByRoleId;
};

const buildRoleDataMaps = (roles) => {
  const roleAssociatedFeatureIdsByRoleId = {};
  const roleAssignedActionIdsByRoleId = buildAssignedActionIdsByRoleId(roles);

  roles.forEach((role) => {
    roleAssociatedFeatureIdsByRoleId[role.id] = new Set(
      role.associatedFeatures.map(({ featureId }) => featureId),
    );
  });

  return {
    roleAssociatedFeatureIdsByRoleId,
    roleAssignedActionIdsByRoleId,
  };
};

const getExportRows = (clientFeatures, featureActionsByFeatureId) => {
  const sortedFeatures = sortBy(clientFeatures, ['module', 'path']);
  const rows = [];

  sortedFeatures.forEach((feature) => {
    rows.push({
      type: ROW_TYPE.PAGE,
      featureId: feature.id,
      module: feature.module,
      label: feature.name,
    });

    const featureActions = featureActionsByFeatureId[feature.id] ?? [];
    const sortedActions = sortBy(featureActions, 'translationKey');

    sortedActions.forEach((action) => {
      rows.push({
        type: ROW_TYPE.RIGHT,
        featureId: feature.id,
        module: feature.module,
        actionId: action.id,
        action,
        label: `${RIGHT_ROW_INDENT}${getActionLabel(action)}`,
      });
    });
  });

  return rows;
};

const getRoleValueForRow = (row, role, roleDataMaps) => {
  const associatedFeatureIds = roleDataMaps.roleAssociatedFeatureIdsByRoleId[role.id];
  const assignedActionIds = roleDataMaps.roleAssignedActionIdsByRoleId[role.id];
  const hasPage = associatedFeatureIds.has(row.featureId);

  if (row.type === ROW_TYPE.PAGE) {
    if (hasPage) {
      return i18next.t('GENERAL.YES');
    }

    return i18next.t('GENERAL.NO');
  }

  if (isViewRight(row.action)) {
    return hasPage ? i18next.t('GENERAL.YES') : i18next.t('GENERAL.NO');
  }

  if (assignedActionIds.has(row.actionId)) {
    return i18next.t('GENERAL.YES');
  }

  return i18next.t('GENERAL.NO');
};

export const exportRoles = (roles, clientFeatures) => {
  const sortedRoles = sortBy(roles, 'name');
  const featureActionsByFeatureId = buildFeatureActionsByFeatureId(roles, clientFeatures);
  const roleDataMaps = buildRoleDataMaps(roles);
  const rows = getExportRows(clientFeatures, featureActionsByFeatureId);

  const headers = [
    i18next.t('GENERAL.MODULE'),
    i18next.t('BACKOFFICE.ROLES.EXPORT_PAGES_AND_RIGHTS_COLUMN'),
    ...sortedRoles.map((role) => upperFirst(role.name)),
  ];

  const data = rows.map((row) => [
    row.module,
    row.label,
    ...sortedRoles.map((role) => getRoleValueForRow(row, role, roleDataMaps)),
  ]);

  const sheet = {
    title: i18next.t('BACKOFFICE.ROLES.EXPORT_SHEET_NAME'),
    headers,
    data,
  };

  const filename = i18next.t('FEATURE.BACKOFFICE.ROLES');

  utilsXLS.makeXLS(filename, [sheet]);
};
